import { useState, useEffect } from 'react';
import { IProfileName, UserService } from '../../services/user.service';
import { WaybillStatus, CheckStatus } from '../../types/type';
import { fullName } from '../../utils/utils';
import './waybillDetails.css';

interface IWaybill {
  id: string;
  num: number;
  date: string;
  time: string;
  status: WaybillStatus;
  carBrand: string;
  carNumber: string;
  odometer: number;
  validUntil: string;
  driverName: string;
  operationType: string;
  operationDescription: string;
  originalData?: any;
}

interface WaybillDetailsProps {
  waybill: IWaybill;
  mappedItem: any;
}

// Функция для получения имени специалиста
const getSpecialistName = async (profileId: string): Promise<IProfileName | null> => {
  if (!profileId) return null;

  try {
    return await UserService.getProfileName(profileId);
  } catch (error) {
    console.error('Ошибка при получении данных специалиста:', error);
    return null;
  }
};

// Функция для получения текста статуса технической проверки
const getTechStatusText = (status: CheckStatus | '') => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'Положительный';
    case 'rejected':
      return 'Отклонен техником';
    case 'canceled':
      return 'Отменен по времени';
    case 'inprogress':
      return 'В процессе';
    case 'waiting':
      return 'Ожидает';
    default:
      return 'Неизвестно';
  }
};

// Функция для получения текста статуса медицинской проверки
const getMedStatusText = (status: CheckStatus | '') => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'Пройдена';
    case 'rejected':
      return 'Отклонен медиком';
    case 'canceled':
      return 'Отменен по времени';
    case 'inprogress':
      return 'В процессе';
    case 'waiting':
      return 'Ожидает';
    default:
      return 'Неизвестно';
  }
};

// Функция для получения описания технической проверки
const getTechStatusDescription = (status: CheckStatus | '', specialist?: IProfileName | null) => {
  const specialistInfo = specialist?.displayName ? ` (Спец: ${specialist.displayName})` : '';

  switch (status.toLowerCase()) {
    case 'completed':
      return `Техническая проверка пройдена успешно, транспортное средство допущено к эксплуатации${specialistInfo}`;
    case 'rejected':
      return `Техническая проверка не пройдена, выявлены нарушения, транспортное средство не допущено к эксплуатации${specialistInfo}`;
    case 'canceled':
      return `Техническая проверка отменена по времени${specialistInfo}`;
    case 'inprogress':
      return `Техническая проверка в процессе выполнения${specialistInfo}`;
    case 'waiting':
      return `Ожидание технической проверки${specialistInfo}`;
    default:
      return 'Техническая проверка не проводилась';
  }
};

// Функция для получения описания медицинской проверки
const getMedStatusDescription = (status: CheckStatus | '', specialist?: IProfileName | null) => {
  const specialistInfo = specialist?.displayName ? ` (Спец: ${specialist.displayName})` : '';

  switch (status.toLowerCase()) {
    case 'completed':
      return `Медицинская проверка пройдена, водитель допущен к управлению транспортным средством${specialistInfo}`;
    case 'rejected':
      return `Медицинская проверка не пройдена, водитель не допущен к управлению транспортным средством${specialistInfo}`;
    case 'canceled':
      return `Медицинская проверка отменена по времени${specialistInfo}`;
    case 'inprogress':
      return `Медицинская проверка в процессе выполнения${specialistInfo}`;
    case 'waiting':
      return `Ожидание медицинской проверки${specialistInfo}`;
    default:
      return 'Медицинская проверка не проводилась';
  }
};

const WaybillDetails: React.FC<WaybillDetailsProps> = ({ waybill, mappedItem }) => {
  const [techSpecialist, setTechSpecialist] = useState<IProfileName | null>(null);
  const [medSpecialist, setMedSpecialist] = useState<IProfileName | null>(null);

  useEffect(() => {
    // Загружаем данные специалистов при монтировании компонента
    if (mappedItem?.techlist?.technicID) {
      getSpecialistName(mappedItem.techlist.technicID).then(setTechSpecialist);
    }
    if (mappedItem?.medlist?.medicID) {
      getSpecialistName(mappedItem.medlist.medicID).then(setMedSpecialist);
    }
  }, [mappedItem]);

  return (
    <div className="waybill-details-new">
      <div className="details-sections">
        {/* Водитель */}
        <div className="detail-section">
          <div className="section-label">Водитель</div>
          <div className="section-value driver-name">
            {fullName({
              lastName: mappedItem?.driverLastName,
              firstName: mappedItem?.driverFirstName,
              middleName: mappedItem?.driverMiddleName,
            }) || 'Не указан'}
          </div>
        </div>

        {/* Техническая проверка */}
        <div className="detail-section">
          <div className="section-label">Тех. осмотр</div>
          <div className="section-value">
            <div className="section-status">{getTechStatusText(mappedItem?.techlist?.checkStatus || '')}</div>
            <div className="section-description">
              {getTechStatusDescription(mappedItem?.techlist?.checkStatus || '', techSpecialist)}
            </div>
          </div>
        </div>

        {/* Медицинская проверка */}
        <div className="detail-section">
          <div className="section-label">Мед. проверка</div>
          <div className="section-value">
            <div className="section-status">{getMedStatusText(mappedItem?.medlist?.status || '')}</div>
            <div className="section-description">
              {getMedStatusDescription(mappedItem?.medlist?.status || '', medSpecialist)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaybillDetails;
